import { useState, useContext } from "react";
import { IoWarningOutline } from "react-icons/io5";
import { PiBellRinging } from "react-icons/pi";
import { FiChevronDown, FiChevronRight, FiDownload, FiInfo } from "react-icons/fi";
import AuthContext from "../../context/AuthContext.js";
import DownloadAlarmPDF from "../../utils/DownloadAlarmPDF.js";
import DownloadWarningPDF from "../../utils/DownloadWarningPDF.js";
import RulesDialog from "./RulesDialog.js";
import api from "../../services/AxiosConnection.js";

const AlarmsAndWarnings = ({ alarms, warnings, trainId }) => {
  const { selectedSite, selectedSiteName } = useContext(AuthContext);
  const [isAlarmsCollapsed, setIsAlarmsCollapsed] = useState(false);
  const [isWarningsCollapsed, setIsWarningsCollapsed] = useState(false);
  const [isAlarmLoading, setIsAlarmLoading] = useState(false);
  const [isWarningLoading, setIsWarningLoading] = useState(false);
  const [isRulesLoading, setIsRulesLoading] = useState(false);
  const [rulesData, setRulesData] = useState(null);
  const [isRulesDialogOpen, setIsRulesDialogOpen] = useState(false);

  const toggleAlarmsCollapse = () => {
    setIsAlarmsCollapsed((prev) => !prev);
  };

  const toggleWarningsCollapse = () => {
    setIsWarningsCollapsed((prev) => !prev);
  };

  const handleDownloadAlarms = () => {
    // console.log("handleDownloadAlarms called, alarms:", alarms);
    if (!alarms || alarms.length === 0 || !alarms[0]?.TrainId) {
      console.warn("No alarm data or Train ID available:", {
        alarmsExists: !!alarms,
        alarmsLength: alarms?.length,
        trainId: alarms?.[0]?.TrainId,
      });
      alert("No alarm data available for this train to download PDF.");
      return;
    }
    const trainDetails = { TrainId: alarms[0].TrainId };
    DownloadAlarmPDF(selectedSite, selectedSiteName, trainDetails, setIsAlarmLoading);
  };

  const handleDownloadWarnings = () => {
    // console.log("handleDownloadWarnings called, warnings:", warnings);
    if (!warnings || warnings.length === 0 || !warnings[0]?.TrainId) {
      console.warn("No warning data or Train ID available:", {
        warningsExists: !!warnings,
        warningsLength: warnings?.length,
        trainId: warnings?.[0]?.TrainId,
      });
      alert("No warning data available for this train to download PDF.");
      return;
    }
    const trainDetails = { TrainId: warnings[0].TrainId };
    DownloadWarningPDF(selectedSite, selectedSiteName, trainDetails, setIsWarningLoading);
  };

  const handleViewRules = async (trainId) => {
    if (!trainId) return;
    setRulesData({ trainValues: { TrainId: trainId } }); // Set partial data for instant feedback
    setIsRulesDialogOpen(true);
    setIsRulesLoading(true);
    try {
      const response = await api.get("/simple-view/getAlarmWarningRulesByTrainId", {
        params: {
          siteDb: selectedSite,
          trainId: trainId,
        },
      });
      if (response.data && response.data.success) {
        setRulesData(response.data);
      } else {
        alert(response.data?.message || "Failed to fetch rules.");
        setIsRulesDialogOpen(false);
      }
    } catch (error) {
      console.error("Error fetching rules:", error);
      alert("An error occurred while fetching rules.");
      setIsRulesDialogOpen(false);
    } finally {
      setIsRulesLoading(false);
    }
  };

  // Function to determine side display
  const getSideDisplay = (side) => {
    if (["Ts1", "Ts3", "Ts5"].includes(side)) return "Left";
    if (["Ts2", "Ts4", "Ts6"].includes(side)) return "Right";
    return side || "N/A";
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
      {/* Alarms */}
      <div className="rounded-lg overflow-hidden border">
        <div className="bg-red-500 px-4 py-3 text-white font-bold relative">
          {/* Centered title and icon */}
          <div className="flex justify-center items-center gap-2">
            <PiBellRinging className="h-5 w-5" />
            <span className="text-sm sm:text-base">LIST OF ALARM</span>
          </div>
          {/* Right buttons */}
          <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
            <button
              onClick={() => handleViewRules(trainId || alarms?.[0]?.TrainId)}
              className="text-white hover:text-gray-300 transition disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="View Rules"
              disabled={isRulesLoading || (!trainId && (!alarms || alarms.length === 0))}
            >
              {isRulesLoading ? (
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : (
                <FiInfo size={18} />
              )}
            </button>
            <button
              onClick={handleDownloadAlarms}
              className="text-white hover:text-gray-300 transition disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Download Alarms"
              disabled={isAlarmLoading || !alarms || alarms.length === 0}
            >
              {isAlarmLoading ? (
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : (
                <FiDownload size={18} />
              )}
            </button>
            <button
              onClick={toggleAlarmsCollapse}
              className="text-white hover:text-gray-300 transition"
              aria-label="Toggle Alarms Table"
            >
              {isAlarmsCollapsed ? <FiChevronRight size={18} /> : <FiChevronDown size={18} />}
            </button>
          </div>
        </div>

        {!isAlarmsCollapsed && (
          <div className="bg-white overflow-x-auto max-h-[400px] scrollbar-custom">
            <table className="min-w-full">
              <thead className="bg-gray-200 sticky top-0 z-9">
                <tr>
                  <th className="px-4 py-2 text-center text-xs">Axle No</th>
                  <th className="px-4 py-2 text-center text-xs">Coach Position</th>
                  <th className="px-4 py-2 text-center text-xs">Coach Number</th>
                  <th className="px-4 py-2 text-center text-xs">Axle Side</th>
                  <th className="px-4 py-2 text-center text-xs">Axle Temp (°C)</th>
                </tr>
              </thead>
              <tbody>
                {alarms && alarms.length > 0 ? (
                  alarms.map((alarm) => (
                    <tr className="hover:bg-gray-50" key={alarm.ID}>
                      <td className="px-4 py-2 text-center text-xs">{alarm.AxelNo || "N/A"}</td>
                      <td className="px-4 py-2 text-center text-xs">{alarm.CoachPosition || "N/A"}</td>
                      <td className="px-4 py-2 text-center text-xs">{alarm.CoachNo || "N/A"}</td>
                      <td className="px-4 py-2 text-center text-xs">{getSideDisplay(alarm.Side)}</td>
                      <td className="px-4 py-2 text-center text-xs">
                        {alarm.Temperature ? Number(alarm.Temperature).toFixed(2) : "N/A"} °C
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-4 py-2 text-center text-xs">
                      No alarms available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Warnings */}
      <div className="rounded-lg overflow-hidden border">
        <div className="bg-yellow-500 px-4 py-3 text-white font-bold relative">
          {/* Centered title and icon */}
          <div className="flex justify-center items-center gap-2">
            <IoWarningOutline className="h-5 w-5" />
            <span className="text-sm sm:text-base">LIST OF WARNING</span>
          </div>
          {/* Right buttons */}
          <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
            <button
              onClick={() => handleViewRules(trainId || warnings?.[0]?.TrainId)}
              className="text-white hover:text-gray-300 transition disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="View Rules"
              disabled={isRulesLoading || (!trainId && (!warnings || warnings.length === 0))}
            >
              {isRulesLoading ? (
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : (
                <FiInfo size={18} />
              )}
            </button>
            <button
              onClick={handleDownloadWarnings}
              className="text-white hover:text-gray-300 transition disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Download Warnings"
              disabled={isWarningLoading || !warnings || warnings.length === 0}
            >
              {isWarningLoading ? (
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : (
                <FiDownload size={18} />
              )}
            </button>
            <button
              onClick={toggleWarningsCollapse}
              className="text-white hover:text-gray-300 transition"
              aria-label="Toggle Warnings Table"
            >
              {isWarningsCollapsed ? <FiChevronRight size={18} /> : <FiChevronDown size={18} />}
            </button>
          </div>
        </div>

        {!isWarningsCollapsed && (
          <div className="bg-white overflow-x-auto max-h-[400px] scrollbar-custom">
            <table className="min-w-full">
              <thead className="bg-gray-200 sticky top-0 z-9">
                <tr>
                  <th className="px-4 py-2 text-center text-xs">Axle No</th>
                  <th className="px-4 py-2 text-center text-xs">Coach Position</th>
                  <th className="px-4 py-2 text-center text-xs">Coach Number</th>
                  <th className="px-4 py-2 text-center text-xs">Axle Side</th>
                  <th className="px-4 py-2 text-center text-xs">Axle Temp (°C)</th>
                </tr>
              </thead>
              <tbody>
                {warnings && warnings.length > 0 ? (
                  warnings.map((warning) => (
                    <tr className="hover:bg-gray-50" key={warning.ID}>
                      <td className="px-4 py-2 text-center text-xs">{warning.AxelNo || "N/A"}</td>
                      <td className="px-4 py-2 text-center text-xs">{warning.CoachPosition || "N/A"}</td>
                      <td className="px-4 py-2 text-center text-xs">{warning.CoachNo || "N/A"}</td>
                      <td className="px-4 py-2 text-center text-xs">{getSideDisplay(warning.Side)}</td>
                      <td className="px-4 py-2 text-center text-xs">
                        {warning.Temperature ? Number(warning.Temperature).toFixed(2) : "N/A"} °C
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-4 py-2 text-center text-xs">
                      No warnings available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {rulesData && (
        <RulesDialog
          isOpen={isRulesDialogOpen}
          onClose={() => setIsRulesDialogOpen(false)}
          data={rulesData}
          loading={isRulesLoading}
        />
      )}
    </div>
  );
};

export default AlarmsAndWarnings;